// src/components/CollectionsListActionsSidebar.jsx
// Sidebar d'actions de la page "Toutes les collections" — chaque action porte
// une description courte, contrairement à l'ancienne rangée de 6 boutons bruts
// (« trop liminaires ») qui ne distinguait pas, par ex., "Rapports" (consulter
// l'existant) de "Créer un rapport" (en générer un nouveau).
import React from "react";
import { Link } from "react-router-dom";
import { ScrollText, FilePlus2, FolderPlus, Upload, Download } from "lucide-react";
import ExportButton from "./shared/ExportButton";

const BORDER = "#e5e3df";
const GOLD = "#a9843f";

const sectionTitle = {
  fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase",
  color: "#8a8378", fontFamily: "system-ui, sans-serif", fontWeight: 600,
  margin: "0 0 10px",
};

function ActionRow({ icon: Icon, title, description, onClick, to, primary }) {
  const content = (
    <>
      <span
        style={{
          flexShrink: 0, width: 30, height: 30, borderRadius: 7,
          background: primary ? GOLD : "#faf6ea", color: primary ? "#fff" : "#8b7355",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <Icon size={15} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>{title}</span>
        <span style={{ display: "block", fontSize: 11.5, color: "#8a8378", lineHeight: 1.4, marginTop: 2 }}>{description}</span>
      </span>
    </>
  );
  const rowStyle = {
    width: "100%", textAlign: "left", padding: "10px 10px", background: "#fff",
    border: `1px solid ${BORDER}`, borderRadius: 6, cursor: "pointer",
    fontFamily: "system-ui, sans-serif", display: "flex", alignItems: "flex-start", gap: 10,
    marginBottom: 8, textDecoration: "none", transition: "border-color 0.15s",
  };

  if (to) {
    return <Link to={to} style={rowStyle}>{content}</Link>;
  }
  return (
    <button type="button" style={rowStyle} onClick={onClick}>
      {content}
    </button>
  );
}

// Description au-dessus, puis le vrai ExportButton (garde son style .btn-artsy
// existant plutôt que de forcer son markup dans ActionRow).
function ExportRow({ label, description }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span
          style={{
            flexShrink: 0, width: 30, height: 30, borderRadius: 7,
            background: "#faf6ea", color: "#8b7355",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Download size={15} />
        </span>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>{label}</span>
      </div>
      <p style={{ fontSize: 11.5, color: "#8a8378", lineHeight: 1.4, margin: "0 0 6px 40px" }}>{description}</p>
      <div style={{ marginLeft: 40 }}>
        <ExportButton label={label} variant="btn-secondary-artsy" format="csv" />
      </div>
    </div>
  );
}

export default function CollectionsListActionsSidebar({ t, onCreateCollection }) {
  return (
    <aside
      style={{
        flex: "0 0 260px", maxWidth: "100%", alignSelf: "flex-start",
        background: "#faf9f7", border: `1px solid ${BORDER}`, borderRadius: 8,
        padding: 16, position: "sticky", top: 16,
      }}
    >
      <div style={sectionTitle}>
        {t("collectionsListSidebar.title", { defaultValue: "Actions" })}
      </div>

      <ActionRow
        icon={FolderPlus}
        title={t("collectionsList.createCollection")}
        description={t("collectionsListSidebar.createCollection.desc", { defaultValue: "Ajouter une nouvelle collection à votre patrimoine." })}
        onClick={onCreateCollection}
        primary
      />

      <ActionRow
        icon={Upload}
        title={t("collectionsList.importZip")}
        description={t("collectionsListSidebar.importZip.desc", { defaultValue: "Importer plusieurs œuvres à la fois depuis un dossier ZIP (photos, factures)." })}
        to="/batch-import"
      />

      <ActionRow
        icon={FilePlus2}
        title={t("collectionsList.createReport")}
        description={t("collectionsListSidebar.createReport.desc", { defaultValue: "Générer un rapport (inventaire, valorisation, assurance, état de conservation…) — chaque type couvre un aspect différent, combinez-les pour une vue complète. Par lots de moins de 50 œuvres pour un PDF fiable." })}
        to="/collections-reports"
      />

      <ActionRow
        icon={ScrollText}
        title={t("collectionsList.reports")}
        description={t("collectionsListSidebar.reports.desc", { defaultValue: "Consulter les rapports déjà générés précédemment." })}
        to="/reports"
      />

      <ExportRow
        label={t("collectionsList.exportAll")}
        description={t("collectionsListSidebar.exportAll.desc", { defaultValue: "Données uniquement (CSV), sans les photos. Pour les images, utilisez « Créer un rapport »." })}
      />
    </aside>
  );
}
